import { RuleType } from "@certa/studio-core/src/constants/rules.constants";
import { INTEGRATION_FIELDS } from "../forms/constants/fields.constants";
import { FieldType } from "@certa/types/src/studio";

export enum FieldTabs {
  ALL = "all",
  TEXT = "text",
  SELECT = "select",
  OPTION = "option",
  FILES = "files",
  SECTIONS = "sections",
  MISC = "misc",
  BASIC = "basic"
}

export enum TemplateTabs {
  ALL = "all",
  MODULE = "module",
  SWIMLANE = "stepgroups",
  STEP = "steps",
  // FIELD = "fields",
  CONNECTOR = "connector",
  MULTI_PROCESS = "multi-process"
}

export const basicFieldTypeData: Record<
  string,
  {
    label: string;
    description?: string;
    tabs?: FieldTabs[];
    ruleTypeBlacklist?: RuleType[];
  }
> = {
  [FieldType.ARRAY]: {
    label: "Array",
    description: "Allows users to enter multiple emails separated by commas",
    tabs: [FieldTabs.TEXT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.BOOLEAN]: {
    label: "Boolean",
    description: "Allows users to respond by selecting either 'Yes' or 'No'",
    tabs: [FieldTabs.OPTION, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.BUSINESS_UNIT]: {
    label: "Business Unit",
    description:
      "Allows users to select a business unit from a dropdown list of predefined business units, that have been added via platform settings",
    tabs: [FieldTabs.SELECT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.CASCADER]: {
    label: "Cascader",
    description:
      "Allows users to select a single value from a multi-level dropdown list",
    tabs: [FieldTabs.SELECT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.DataInheritance,
      RuleType.RelatedProcessDataInheritance
    ]
  },
  [FieldType.CHAR]: {
    label: "Char",
    description:
      "Allows users to enter a maximum of 255 characters, used for short responses such as first and last name, company name, etc",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.CHECKBOX]: {
    label: "Checkbox",
    description:
      "Allows users to select one or more options from a small set of options",
    tabs: [FieldTabs.OPTION, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.CURRENCY]: {
    label: "Currency",
    description:
      "Allows users to enter currency values of a particular currency code",
    tabs: [FieldTabs.SELECT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.NAIVE_DATE]: {
    label: "Date",
    description: "Allows users to select a date that is time-zone agnostic",
    tabs: [FieldTabs.MISC, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.DATA_OBJECTS_SURVEY_TABLE]: {
    label: "Data Objects Survey Table",
    description:
      "Allows users to select objects whose POCs will receive questionnaire links for vendor outreach",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.DATE]: {
    label: "Date (timezone aware)",
    description:
      "Allows users to select a date from a calendar view that is displayed as per the user's timezone",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.DOWNLOAD_ATTACHMENT]: {
    label: "Download Attachment",
    description: "Allows users to download a file that has been pre-uploaded",
    tabs: [FieldTabs.FILES],
    ruleTypeBlacklist: [
      RuleType.DataInheritance,
      RuleType.RelatedProcessDataInheritance
    ]
  },
  [FieldType.DROPDOWN_SINGLE]: {
    label: "Dropdown (Single)",
    description:
      "Allows users to select a single option from a drop-down list of options",
    tabs: [FieldTabs.SELECT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.DROPDOWN_MULTI]: {
    label: "Dropdown (Multi)",
    description:
      "Allows users to select multiple options from a drop-down list of options",
    tabs: [FieldTabs.SELECT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.DYNAMIC_GROUP_JSON]: {
    label: "Dynamic Group JSON",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.DataInheritance,
      RuleType.RelatedProcessDataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger
    ]
  },
  [FieldType.EMAIL]: {
    label: "Email",
    description:
      "Allows users to enter an email address and highlights if the format is invalid",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.FREE_TEXT]: {
    label: "Free Text",
    description:
      "Allows users to input over 10k+ characters of text, used for longer responses such as descriptions",
    tabs: [FieldTabs.MISC, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.EMBED_VIDEO]: {
    label: "Embed Video",
    description:
      "A place to embed a video from a third party video hosting platform",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.RelatedProcessDataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger
    ]
  },
  [FieldType.GOOGLE_ADDRESS_SEARCH]: {
    label: "Google Address Search",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.GROUPED_CHECKBOX]: {
    label: "Grouped Checkbox",
    description:
      "Allows users to select multiple values from a list of sectionally segregated options",
    tabs: [FieldTabs.OPTION],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.DataInheritance,
      RuleType.RelatedProcessDataInheritance
    ]
  },
  [FieldType.WHITESPACE]: {
    label: "Horizontal Space",
    description:
      "Horizontal whitespace between adjacent elements for better visual segregation",
    tabs: [FieldTabs.SECTIONS],
    ruleTypeBlacklist: [
      RuleType.DataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger,
      RuleType.RelatedProcessDataInheritance,
      RuleType.ReadOnlyFieldConstraints
    ]
  },
  [FieldType.INFO_DESCRIPTION_BOX]: {
    label: "Info/Description Box",
    description:
      "A message that can be displayed as an error, warning, or information",
    tabs: [FieldTabs.SECTIONS],
    ruleTypeBlacklist: [
      RuleType.DataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger,
      RuleType.RelatedProcessDataInheritance,
      RuleType.ReadOnlyFieldConstraints
    ]
  },
  [FieldType.JSON]: {
    label: "JSON Code",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.LC_MESSAGE]: {
    label: "Last Email Activity",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.RAG,
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.ChildProcessCreation,
      RuleType.DataInheritance,
      RuleType.DataReduction,
      RuleType.IntegrationTrigger,
      RuleType.IntegrationPolling,
      RuleType.RelatedProcessDataInheritance
    ]
  },
  [FieldType.MULTI_FILE]: {
    label: "Multi File Upload",
    description:
      "Allows users to upload multiple files. The supported formats are (pdf, doc, docx, xls, xlsx, csv, txt, png, jpg, jpeg, cer, msg, zip, ppt, pptx, mp4, mov)",
    tabs: [FieldTabs.FILES],
    ruleTypeBlacklist: [RuleType.DataInheritance]
  },
  [FieldType.DECIMAL]: {
    label: "Number (decimal)",
    description: "Allows users to enter decimal numbers",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.INTEGER]: {
    label: "Number (integer)",
    description: "Allows users to enter whole integers",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.OWNER]: {
    label: "Owner",
    description: "Allows users to select the owner of the process",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.RelatedProcessDataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger
    ]
  },
  [FieldType.PDF_LINK]: {
    label: "PDF Link",
    description:
      "Displays a URL for the PDF generated from a specific PDF configuration",
    tabs: [FieldTabs.FILES],
    ruleTypeBlacklist: [
      RuleType.DataInheritance,
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.PDF_VIEWER]: {
    label: "PDF Viewer"
  },
  [FieldType.PHONE]: {
    label: "Phone",
    description:
      "Allows users to select a country code and enter a contact number",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.PROCESS_LINKING_ALPHA]: {
    label: "Process Linking (alpha feature)",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.DataInheritance,
      RuleType.RelatedProcessDataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger
    ]
  },
  [FieldType.QUESTIONNAIRE_LIST]: {
    label: "Questionnaire List",
    description:
      "Allows users to select from a list of available questionnaires",
    tabs: [FieldTabs.SELECT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.RADIO]: {
    label: "Radio",
    description:
      "Allows users to select a single option from a small set of options",
    tabs: [FieldTabs.OPTION, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.PARAGRAPH]: {
    label: "Section Header",
    description:
      "A static text block that can be used for adding headings and subheadings to the forms",
    tabs: [FieldTabs.TEXT, FieldTabs.SECTIONS, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.DataInheritance,
      RuleType.RelatedProcessDataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger,
      RuleType.ReadOnlyFieldConstraints
    ]
  },
  [FieldType.REGION]: {
    label: "Region",
    description:
      "Allows users to select a region from a dropdown list of predefined regions, that have been added via platform settings",
    tabs: [FieldTabs.SELECT],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.RICH_TEXT_EDITOR]: {
    label: "Rich Text Editor",
    description:
      "Allows users to enter text which can be formatted and stylised in various ways",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.SECTION_BREAK]: {
    label: "Section line",
    description:
      "Full-width horizontal line for vertically spacing adjacent elements",
    tabs: [FieldTabs.SECTIONS],
    ruleTypeBlacklist: [
      RuleType.DataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger,
      RuleType.RelatedProcessDataInheritance,
      RuleType.ReadOnlyFieldConstraints
    ]
  },
  [FieldType.SLIDER]: {
    label: "Slider",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.SUPPLIER_CREATION]: {
    label: "Supplier Creation",
    description:
      "Allows users to enter an email address to invite third-parties to log in to the system",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.SYSTEM]: {
    label: "System Field",
    description:
      "A field that contains system specific information related to various aspects of the system itself",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.SCREENING_UPDATE_EVENTS]: {
    label: "Screening Update Events",
    description:
      "Used to render monitoring objects from screening objects. Stores a list of object IDs as a tilde-separated string",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.TABLE]: {
    label: "Table",
    description:
      "Allows users to enter data in a tabular format with pre-defined columns and validations",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.FILE]: {
    label: "Upload File",
    description:
      "Allows users to upload a single file. The supported formats are (pdf, doc, docx, xls, xlsx, csv, txt, png, jpg, jpeg, cer, msg, zip, ppt, pptx, mp4, mov)",
    tabs: [FieldTabs.FILES],
    ruleTypeBlacklist: [RuleType.DataInheritance]
  },
  [FieldType.URL]: {
    label: "URL",
    description:
      "Allows a website address to be entered or displayed, and highlights if the format is invalid",
    tabs: [FieldTabs.TEXT, FieldTabs.BASIC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2
    ]
  },
  [FieldType.VISUALIZATION]: {
    label: "Visualization",
    description:
      "Displays a graphical depiction of the value stored in this field, between a range and colour coded as specified by the business rules",
    tabs: [FieldTabs.MISC],
    ruleTypeBlacklist: [
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger,
      RuleType.ChildProcessCreation
    ]
  },
  [FieldType.WIZARD_SECTION]: {
    label: "Wizard Section",
    tabs: [FieldTabs.SECTIONS],
    ruleTypeBlacklist: [
      RuleType.DataInheritance,
      RuleType.RAG,
      RuleType.DataReduction,
      RuleType.ChildProcessCreation,
      RuleType.AttachmentInheritance,
      RuleType.AttachmentInheritanceV2,
      RuleType.RelatedWorkflowFileConstraintsV2,
      RuleType.IntegrationPolling,
      RuleType.IntegrationTrigger,
      RuleType.RelatedProcessDataInheritance,
      RuleType.ReadOnlyFieldConstraints
    ]
  },
  [FieldType.WORD_DOCUMENT]: {
    label: "Word Document Download",
    description:
      "Allows users to download a pre-defined Word document, which is populated with data from the process it is contained in",
    tabs: [FieldTabs.FILES],
    ruleTypeBlacklist: [RuleType.DataInheritance]
  }
};

export const integrationFieldTypeData: Record<
  string,
  { label: string; description?: string }
> = {
  // Integration
  [FieldType.SCREENING]: {
    label: "Screening"
  },
  [FieldType.DNB_DUNS_SEARCH]: {
    label: "DNB DUNS Search"
  },
  [FieldType.DNB_DUNS_SEARCH_DIRECT_PLUS]: {
    label: "DNB DUNS Search Direct Plus"
  },
  [FieldType.DNB_COMPANY_PROFILE]: {
    label: "DNB Company Profile"
  },
  [FieldType.DNB_RISK_SCORE]: {
    label: "DNB Risk Score"
  },
  [FieldType.DNB_UBO]: {
    label: "DNB UBO"
  },
  [FieldType.DNB_UBO1]: {
    label: "DNB UBO 1"
  },
  [FieldType.DNB_UBO_GRAPH]: {
    label: "DNB UBO Graph"
  },
  [FieldType.DNB_DIRECTORS]: {
    label: "DNB Directors"
  },
  [FieldType.DNB_RDC]: {
    label: "DNB RDC"
  },
  [FieldType.DNB_RDC1]: {
    label: "DNB RDC 1"
  },
  [FieldType.DNB_RDC_ALERTS]: {
    label: "DNB RDC Alerts"
  },
  [FieldType.DNB_CMP_ENT_VW]: {
    label: "DNB CMP ENT VW"
  },
  [FieldType.DNB_CMPCVF]: {
    label: "DNB CMPCVF"
  },
  [FieldType.DNB_FAMILYTREE]: {
    label: "DNB Family Tree"
  },
  [FieldType.DNB_FAMILYTREE_BRANCH]: {
    label: "DNB Family Tree Branch"
  },
  [FieldType.DNB_DATA_READER]: {
    label: "DNB Data Reader"
  },
  [FieldType.DNB_FINANCIALS]: {
    label: "DNB Financials"
  },
  [FieldType.DNB_LITIGATION]: {
    label: "DNB Litigation"
  },
  [FieldType.IBAN_SEARCH]: {
    label: "IBAN Search"
  },
  [FieldType.EU_VAT_CHECK]: {
    label: "EU VAT Check"
  },
  [FieldType.US_TIN_CHECK]: {
    label: "US TIN Check"
  },
  [FieldType.CHARITY_CHECK]: {
    label: "Charity Check"
  },
  [FieldType.WHOIS_SEARCH]: {
    label: "WHOIS Search"
  },
  [FieldType.CLEARBIT_SEARCH]: {
    label: "ClearBit Search"
  },
  [FieldType.CSV_SEARCH]: {
    label: "CSV Search"
  },
  [FieldType.LN_SEARCH]: {
    label: "LN Search"
  },
  [FieldType.SALESFORCE]: {
    label: "Salesforce"
  },
  [FieldType.GOOGLE_SEARCH]: {
    label: "Google Search"
  },
  [FieldType.TRANSLATION]: {
    label: "Translation"
  },
  [FieldType.TRANSLITERATION]: {
    label: "Transliteration"
  },
  [FieldType.THOMSON_REUTERS_GROUP]: {
    label: "Thomson Reuters Group"
  },
  [FieldType.THOMSON_REUTERS_CASE]: {
    label: "Thomson Reuters Case"
  },
  [FieldType.THOMSON_REUTERS_SCREEN]: {
    label: "Thomson Reuters Screen"
  },
  [FieldType.THOMSON_REUTERS_SCREEN_SYNC]: {
    label: "Thomson Reuters Screen Sync"
  },
  [FieldType.THOMSON_REUTERS_SCREENRESULT]: {
    label: "Thomson Reuters Screen Result"
  },
  [FieldType.THOMSON_REUTERS_WCPROFILE]: {
    label: "Thomson Reuters WC Profile"
  },
  [FieldType.THOMSON_REUTERS_CASESYSTEMID]: {
    label: "Thomson Reuters Case System ID"
  },
  [FieldType.THOMSON_REUTERS_RESOLVE_RESULT]: {
    label: "Thomson Reuters Resolve Result"
  },
  [FieldType.HERMES]: {
    label: "Hermes"
  },
  [FieldType.HERMES_2]: {
    label: "Hermes 2"
  },
  [FieldType.HERMES_DNB_DIVERSITY_2]: {
    label: "Hermes DNB Diversity 2"
  },
  [FieldType.CHILD_WORKFLOW]: {
    label: "Child Workflow"
  },
  [FieldType.DUPLICATE_CHECK]: {
    label: "Duplicate Check"
  },
  [FieldType.APPBUILDER_DUPLICATE_CHECK]: {
    label: "Appbuilder Duplicate Check"
  },
  [FieldType.CSI]: {
    label: "CSI"
  },
  [FieldType.BOEING_SCREENING_STATUS]: {
    label: "Boeing Screening Status"
  },
  [FieldType.BOEING_ADJUDICATION_STATUS]: {
    label: "Boeing Adjudication Status"
  },
  [FieldType.DNB_GBO]: {
    label: "DNB GBO"
  },
  [FieldType.RDC_EVENT_DETAILS]: {
    label: "RDC Event Details"
  },
  [FieldType.GREYLIST_CHECK]: {
    label: "GreyList Check"
  },
  [FieldType.PM_GREYLIST_CHECK]: {
    label: "PM GreyList Check"
  },
  [FieldType.RDC_ALERT_METADATA]: {
    label: "RDC Alert Metadata"
  },
  [FieldType.DNB_INITIATE_INVESTIGATION]: {
    label: "DNB Initiate Investigation"
  },
  [FieldType.DNB_INVESTIGATION_STATUS]: {
    label: "DNB Investigation Status"
  },
  [FieldType.OCR]: {
    label: "OCR"
  },
  [FieldType.DOCUSIGN]: {
    label: "DocuSign"
  },
  [FieldType.KRYPTON_AI_PUSH]: {
    label: "Krypton AI Push"
  },
  [FieldType.ADOBE_SIGN]: {
    label: "Adobe Sign"
  },
  [FieldType.AMBER_ROAD]: {
    label: "Amber Road"
  },
  [FieldType.CREATE_REUSABLE_COMPONENT]: {
    label: "Create Reusable Component"
  },
  [FieldType.CODE_MIGRATION]: {
    label: "Code Migration"
  },
  [FieldType.PLATFORM_SETTINGS]: {
    label: "Platform Settings"
  },
  [FieldType.GOOGLE_ADDRESS_SEARCH]: {
    label: "Google Address Search"
  },
  [FieldType.WORKFLOW_STATUS]: {
    label: "Workflow Status"
  },
  [FieldType.INTEGRATION_WORD_DOCUMENT_DOWNLOAD]: {
    label: "Word Document Download"
  },
  [FieldType.DOCUSIGN_DOWNLOAD]: {
    label: "DocuSign Download"
  },
  [FieldType.RDC_STOP_INQ]: {
    label: "RDC Stop INQ"
  },
  [FieldType.JUXTAPOSE]: {
    label: "Juxtapose"
  },
  [FieldType.IMAGE]: {
    label: "Image"
  },
  [FieldType.WORKDAY]: {
    label: "Workday"
  },
  [FieldType.HERMES_DNB_DIVERSITY]: {
    label: "Hermes DNB Diversity"
  },
  [FieldType.COMPLY_EXCHANGE]: {
    label: "Comply Exchange"
  },
  [FieldType.WORKFLOW_LOGO]: {
    label: "Workflow Logo"
  },
  [FieldType.PACKAGE_DEPLOY]: {
    label: "Package Deploy"
  },
  [FieldType.CODEDEPLOY_TO_ENV]: {
    label: "Code Deploy To Env"
  },
  [FieldType.PUBLISHING]: {
    label: "Publishing"
  },
  [FieldType.E_SIGNATURE]: {
    label: "E-Signature"
  }
};

export const systemFieldSourceMappingOptions = [
  {
    label:
      "Business unit(s) assigned to the user who created the workflow (Full Access)",
    value: "workflow.created_by.profile.business_unit_full_access.all.name"
  },
  {
    label:
      "Business unit(s) assigned to the user who created the workflow (Read Access)",
    value: "workflow.created_by.profile.business_read_full_access.all.name"
  },
  {
    label: "Email of User who completed the step",
    value: "step.completed_by.email"
  },
  {
    label: "Username of User who completed the step",
    value: "step.completed_by.username"
  },
  {
    label: "First Name of User who completed the step",
    value: "step.completed_by.first_name"
  },
  {
    label: "Last Name of User who completed the step",
    value: "step.completed_by.last_name"
  },
  {
    label: "Full Name of User who completed the step",
    value: "step.completed_by"
  },
  {
    label:
      "Region(s) assigned to the user who created the workflow (Full Access)",
    value: "workflow.created_by.profile.region_full_access.all.name_std"
  },
  {
    label:
      "Region(s) assigned to the user who created the workflow (Read Access)",
    value: "workflow.created_by.profile.region_read_access.all.name_std"
  },
  {
    label: "Step Completion Date",
    value: "step.completed_at.isoformat_naive_date"
  },
  {
    label: "Step Completion Date and Time",
    value: "step.completed_at.isoformat"
  },
  {
    label: "Step Definition ID and Step Name",
    value: "step.definition_id"
  },
  {
    label: "Step Enabled/Visible",
    value: "step.is_enabled"
  },
  {
    label: "Step ID",
    value: "step.id"
  },
  {
    label: "Step Initiation Date",
    value: "step.initiated_at.isoformat_naive_date"
  },
  {
    label: "Step Initiation Date and Time",
    value: "step.initiated_at.isoformat"
  },
  {
    label: "Step Last Updated Date",
    value: "step.updated_at.isoformat_naive_date"
  },
  {
    label: "Step Last Updated Date and Time",
    value: "step.updated_at.isoformat"
  },
  {
    label: "Step Locked",
    value: "step.is_locked"
  },
  {
    label: "Step Overdue",
    value: "step.overdue"
  },
  {
    label: "Step Submission Count",
    value: "step.submit_count"
  },
  {
    label: "Stepgroup ID and Stepgroup Name",
    value: "step.step_group_id"
  },
  {
    label: "Stepgroup ID",
    value: "step.step_group.id"
  },
  {
    label: "Workflow Name",
    value: "workflow.name"
  },
  {
    label: "User role(s) assigned to the user who created the workflow",
    value: "workflow.created_by.groups.all.name"
  },
  {
    label: "Workflow Business Unit",
    value: "workflow.business_unit.name"
  },
  {
    label: "Workflow Created By - User Email",
    value: "workflow.created_by.email"
  },
  {
    label: "Workflow Created By - User Name",
    value: "workflow.created_by.get_full_name"
  },
  {
    label: "Workflow Creation Date",
    value: "workflow.created_at.isoformat_naive_date"
  },
  {
    label: "Workflow Creation Date and Time",
    value: "workflow.created_at.isoformat"
  },
  {
    label: "Workflow Definition / Version ID",
    value: "workflow.definition"
  },
  {
    label: "Workflow Ext UID",
    value: "workflow.uid"
  },
  {
    label: "Workflow ID",
    value: "workflow.id"
  },
  {
    label: "Workflow ID and Workflow Name",
    value: "step.workflow"
  },
  {
    label: "Workflow Region",
    value: "workflow.region.name_std"
  },
  {
    label: "Workflow Status",
    value: "workflow.status.label"
  },
  {
    label: "Workflow Updated Timestamp",
    value: "workflow.updated_at.isoformat"
  },
  {
    label: "Date Joined",
    value: "workflow.content_object.date_joined"
  },
  {
    label: "Last Login",
    value: "workflow.content_object.last_login"
  },
  {
    label: "User Business Unit - Read Access",
    value: "workflow.content_object.profile.business_unit_read_access.all.name"
  },
  {
    label: "User Business Unit - Full Access",
    value: "workflow.content_object.profile.business_unit_full_access.all.name"
  },
  {
    label: "User Regions - Read Access",
    value: "workflow.content_object.profile.region_read_access.all.name_std"
  },
  {
    label: "User Regions - Full Access",
    value: "workflow.content_object.profile.region_full_access.all.name_std"
  },
  {
    label: "User Permissions - Groups",
    value: "workflow.content_object.groups.all.permissions__codename"
  },
  {
    label: "User Group Permissions",
    value: "workflow.content_object.groups.all.permissions__name"
  },
  {
    label: "User Permissions",
    value: "workflow.content_object.user_permissions.all.name"
  },
  {
    label: "The Subject Of The Latest Email Sent",
    value: "field.workflow.emails.latest.subject"
  },
  {
    label: "The Status Of The Latest Email Sent",
    value: "field.workflow.emails.latest.status"
  },
  {
    label: "The Email-id To Which The Latest Email Is Sent Out",
    value: "field.workflow.emails.latest.email_id"
  },
  {
    label:
      "The Status Code Of An Email In Case Of Bounced, Failed (Visible Only After 72 Hours)",
    value: "field.workflow.emails.latest.smtp_status_code"
  },
  {
    label:
      "The Reject Reason Of An Email In Case Of Bounced, Failed (Visible Only After 72 Hours)",
    value: "field.workflow.emails.latest.reject_reason"
  },
  {
    label: "LC Message Showing Status Of Latest Email ",
    value: "lc_message"
  },
  {
    label: "Permissions Of The User Who Created The Workflow",
    value: "workflow.created_by.user_permissions.all.name"
  }
];

export const AllowedRuleTypesForIntegrationFields = [
  RuleType.FieldVisibility,
  RuleType.IntegrationTrigger,
  RuleType.IntegrationPolling
];

export const ALLOWED_BASE_FIELD_TYPES_FOR_ARRAY_FIELD_TYPE = [
  FieldType.CHAR,
  FieldType.FREE_TEXT,
  FieldType.EMAIL,
  FieldType.DECIMAL,
  FieldType.INTEGER,
  FieldType.PHONE,
  FieldType.URL
];

export const NON_FILTERABLE_FIELDS = [
  FieldType.PARAGRAPH,
  FieldType.INFO_DESCRIPTION_BOX,
  FieldType.SECTION_BREAK,
  FieldType.WIZARD_SECTION,
  FieldType.CHILD_WORKFLOW,
  FieldType.FILE,
  FieldType.DOWNLOAD_ATTACHMENT,
  FieldType.INTEGRATION_WORD_DOCUMENT_DOWNLOAD,
  FieldType.WHITESPACE,
  FieldType.TABLE,
  FieldType.PDF_LINK,
  FieldType.PDF_VIEWER,
  FieldType.DYNAMIC_GROUP_JSON,
  FieldType.EMBED_VIDEO,
  FieldType.GROUPED_CHECKBOX,
  FieldType.JSON,
  FieldType.VISUALIZATION,
  FieldType.CASCADER,
  FieldType.ARRAY,
  FieldType.SCREENING_UPDATE_EVENTS,
  FieldType.GOOGLE_ADDRESS_SEARCH,
  FieldType.IMAGE,
  FieldType.MULTI_FILE,
  FieldType.WORD_DOCUMENT,
  FieldType.PROCESS_LINKING_ALPHA,
  FieldType.RICH_TEXT_EDITOR,
  INTEGRATION_FIELDS,
  ...Object.keys(integrationFieldTypeData)
];

export const UN_HIDEABLE_FIELDS = [
  FieldType.WIZARD_SECTION,
  FieldType.DATA_OBJECTS_SURVEY_TABLE,
  FieldType.QUESTIONNAIRE_LIST
];
